import { sanitizeUrl } from '@braintree/sanitize-url';
import getConfig from 'next/config';
import Router, { NextRouter } from 'next/router';
import { AdditionalPayload } from 'organisms/DataView/DataGridView/types';
import { ITableItem } from 'stores/interfaces';
import { OrderType } from 'stores/networkInterfaces';
import { IStore } from 'stores/store';
import { reportEvent } from 'utils/analytics';
import { IAuthDetails } from 'utils/authUtils';
import { isBrowser, isTextSelection } from 'utils/domUtils';
import { IMenuItem } from 'utils/globalTypes';
import { removeEmpty } from 'utils/object';
import { parseQueryString } from 'utils/urlUtils';

const { publicRuntimeConfig } = getConfig();
const { NEXT_PUBLIC_SCHEDULE_CALL_URL } = publicRuntimeConfig;

export const orderTypeMap = {
  [OrderType.int_buy]: 'buy',
  [OrderType.bid_buy]: 'buy',
  [OrderType.bid_sell]: 'sell'
};

export function navigateToOrder(
  offeringId?: string,
  orderId?: string,
  orderType?: OrderType,
  event?: string
) {
  if (!isTextSelection() && offeringId) {
    const url =
      orderType !== undefined
        ? `/invest/${offeringId}/${orderTypeMap[orderType]}`
        : `/invest/${offeringId}`;
    reportEvent('open_order', {
      offering_id: offeringId,
      order_id: orderId,
      order_type: orderType && OrderType[orderType],
      url: url
    });

    const query = {
      oid: orderId,
      event,
      ...getPersistedParams()
    };

    return navigateToUrl(url, { query: removeEmpty(query) });
  }
}

export function getOfferingUrl(offeringId?: string) {
  if (offeringId) {
    return `/offering/${encodeURIComponent(offeringId)}`;
  }
}

export function getOrderBasePath(offeringId: string, orderType?: number, step?: string) {
  if (!offeringId) return '/404';
  return orderType !== undefined
    ? step
      ? `/invest/${offeringId}/${orderTypeMap[orderType]}/${step}`
      : `/invest/${offeringId}/${orderTypeMap[orderType]}`
    : `/invest/${offeringId}`;
}

export function getOrderUrl(
  offeringId: string | undefined,
  orderType: OrderType,
  orderId?: string,
  step?: string
) {
  if (!offeringId) return '/404';
  return orderId
    ? `${getOrderBasePath(offeringId, orderType, step)}?oid=${encodeURIComponent(orderId)}`
    : `${getOrderBasePath(offeringId, orderType, step)}`;
}

export function navigateToOffering(offeringId?: string) {
  const offeringUrl = getOfferingUrl(offeringId);
  if (!isTextSelection() && offeringUrl) {
    reportEvent('open_offering', {
      offeringId: offeringId,
      url: offeringUrl
    });
    return navigateToUrl(offeringUrl, { query: getPersistedParams() });
  }
}

export interface NavigateToUrlParamsQuery {
  [key: string]: string | string[] | number | boolean | undefined;
}

export interface NavigateToUrlParams {
  newTab?: boolean;
  reload?: boolean;
  query?: NavigateToUrlParamsQuery;
  hash?: string;
  options?: { shallow?: boolean; locale?: string | false; scroll?: boolean };
}

export function navigateToUrl(url?: string, params?: NavigateToUrlParams) {
  if (!url || !isBrowser()) {
    return;
  }

  if (params?.newTab) {
    window.open(sanitizeUrl(url), '_blank');
    return;
  }

  if (params?.reload) {
    window.open(sanitizeUrl(url), '_self');
    return;
  }

  const urlParts = sanitizeUrl(url).split('?');
  const pathName = urlParts[0];
  const queryParams = parseQueryString(urlParts[1]);

  return Router?.push(
    {
      pathname: pathName,
      query: { ...queryParams, ...params?.query, ...getPersistedParams() },
      hash: params?.hash
    },
    undefined,
    params?.options
  );
}

export function filterNavItem(item: IMenuItem, authDetails: IAuthDetails) {
  return (
    (item?.auth === undefined || !!item?.auth == !!authDetails.isAuthenticated) &&
    (item?.approved === undefined || !!item?.approved == !!authDetails.isApproved) &&
    (item?.system === undefined || !!item?.system == authDetails.isSystem) &&
    (item?.management === undefined ||
      !!item?.management == authDetails.isManagement ||
      (item?.system === undefined && !!authDetails.isSystem))
  );
}

export function getPersistedParams() {
  // ToDo: Uncomment the code once is ready to test utm_ query params

  // const res: { [key: string]: string } = {};
  // const PARAMS_TO_PRESERVE_PREFIX = 'utm_';
  // if (!isBrowser()) return;
  // for (const key in Router.query) {
  //   if (!key.startsWith(PARAMS_TO_PRESERVE_PREFIX)) continue;
  //   const val = Router.query[key];
  //   if (typeof val !== 'string') continue;
  //   res[key] = val;
  // }
  // return res;
  return {};
}

const formatUrl = (url: string, query: Record<string, string | number>) => {
  const queryStr = query
    ? Object.entries(query)
        .filter(([key, value]) => value)
        .map((kv) => kv.map(encodeURIComponent).join('='))
        .join('&')
    : undefined;

  return `${url}${queryStr ? `?${queryStr}` : ''}`;
};

export function getScheduleCallUrl(authDetails: IAuthDetails) {
  const url = NEXT_PUBLIC_SCHEDULE_CALL_URL || 'https://www.usremsecurities.com/contact';
  const nameParts = authDetails?.nickname?.split(' ') || [];
  const query = {
    email: authDetails?.email || '',
    firstName: nameParts[0] || '',
    lastName: nameParts[1] || ''
  };
  return formatUrl(url, query);
}

interface OpenDrawerParams {
  store: IStore['ManagementStore'];
  router: NextRouter;
  action: string;
  entity?: string;
  query?: NavigateToUrlParamsQuery;
}

export const openDrawer =
  ({ store, router, action, query }: OpenDrawerParams) =>
  async (
    item: ITableItem,
    onUpdate?: (item: ITableItem, additionalPayload?: AdditionalPayload) => void
  ) => {
    if (!router?.query?.action) return;

    await navigateToUrl(router.pathname, {
      query: { ...router?.query, action, id: item?.id, ...query },
      options: { shallow: true }
    });
    store.setSelectedEntity(item, onUpdate);
  };
